import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../../lib/cn";

/**
 * Panel / Card — surface laiton de base. Toutes les pages composent dessus.
 * Rivets d'angle et filigranes d'angle optionnels pour l'accent médiéval-steampunk.
 */

interface PanelProps extends HTMLAttributes<HTMLDivElement> {
  as?: "div" | "section" | "article";
  rivets?: boolean;
  /** Volutes de laiton gravées aux quatre angles (décor médiéval). */
  filigree?: boolean;
  glow?: "none" | "gold" | "arcane" | "violet" | "ember" | "blood" | "patina";
  /** Élévation feutrée + liseré doré au survol (cartes cliquables). */
  interactive?: boolean;
}

const GLOW: Record<NonNullable<PanelProps["glow"]>, string> = {
  none: "",
  gold: "shadow-glow-gold",
  arcane: "shadow-glow-arcane",
  violet: "shadow-glow-violet",
  ember: "shadow-glow-ember",
  blood: "shadow-glow-blood",
  patina: "shadow-glow-patina",
};

export function Panel({
  as: Tag = "div",
  rivets = false,
  filigree: _filigree = false,
  glow = "none",
  interactive = false,
  className,
  children,
  ...rest
}: PanelProps) {
  return (
    <Tag
      className={cn("etheria-panel min-w-0", GLOW[glow], interactive && "etheria-panel-interactive", className)}
      {...rest}
    >
      {rivets && <Rivets />}
      {children}
    </Tag>
  );
}

/** Têtes de rivets bombées (reflet laiton) aux quatre angles. */
function Rivets() {
  return (
    <>
      <span aria-hidden className="etheria-rivet left-2 top-2" />
      <span aria-hidden className="etheria-rivet right-2 top-2" />
      <span aria-hidden className="etheria-rivet bottom-2 left-2" />
      <span aria-hidden className="etheria-rivet bottom-2 right-2" />
    </>
  );
}

export function PanelHeader({
  title,
  subtitle,
  action,
  icon,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  /** Glyphe/sceau à gauche du titre (ex <WaxSeal>…</WaxSeal>). */
  icon?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "relative flex flex-col gap-3 border-b border-iron-300/45 bg-brass-plate px-4 py-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4 sm:px-5 sm:py-4",
        className,
      )}
    >
      <span aria-hidden className="pointer-events-none absolute inset-y-0 left-0 w-[3px] bg-blood-400/80" />
      <div className="flex min-w-0 items-center gap-3">
        {icon && <span className="shrink-0">{icon}</span>}
        <div className="min-w-0">
          <h3 className="break-words font-display text-base font-semibold uppercase tracking-[0.06em] text-parchment-50 text-engraved sm:truncate">{title}</h3>
          {subtitle && <p className="mt-0.5 break-words text-sm text-parchment-200/60">{subtitle}</p>}
        </div>
      </div>
      {action && <div className="w-full shrink-0 sm:w-auto">{action}</div>}
      <span aria-hidden className="absolute inset-x-0 -bottom-px h-px bg-arcane-400/20" />
    </div>
  );
}

export function PanelBody({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn("min-w-0 p-4 sm:p-5", className)}>{children}</div>;
}
